import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import Login from './components/Login';
import Profile from './components/Profile';
import Homepage from './components/Homepage';
import OfflineContentScreen from './components/OfflineContentScreen';
import SuggestedRoutes from './components/SuggestedRoutes';
import ProtectedRoute from './components/ProtectedRoute';
import OfflineBanner from './components/OfflineBanner';
import SwUpdateToast from './components/SwUpdateToast';
import ApiService from './ApiService';
import * as db from './cache/db';
import { userProfileKey } from './cache/routePreCache';

function App() {
  const { i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(i18n.language);

  const applyProfile = useCallback((profile) => {
    setUserProfile(profile);
    if (profile?.language) {
      i18n.changeLanguage(profile.language);
      setLanguage(profile.language);
    }
  }, [i18n]);

  const getUserProfile = useCallback(async (user) => {
    if (!user || userProfile) return;

    const cacheKey = userProfileKey(user.uid);

    // Offline: skip the network entirely, hydrate from IDB if we have a
    // previously-cached profile. Don't POST a temp profile — we can't create
    // a server-side user without connectivity.
    if (!navigator.onLine) {
      const cached = await db.get(cacheKey).catch(() => null);
      if (cached) applyProfile(cached);
      return;
    }

    let savedUserProfile;
    try {
      savedUserProfile = await ApiService.get('/user/', user);
    } catch {
      // Network failure on what we thought was an online connection — fall
      // through to the cached profile so the app still works.
      const cached = await db.get(cacheKey).catch(() => null);
      if (cached) applyProfile(cached);
      return;
    }

    if (savedUserProfile) {
      applyProfile(savedUserProfile);
      db.set(cacheKey, savedUserProfile).catch(() => { /* IDB failure non-fatal */ });
      return;
    }

    // No profile yet on the server — create one (online path only).
    const tempUserProfile = {};
    if (user.displayName) {
      const nameParts = user.displayName.split(' ');
      tempUserProfile.name = nameParts[0] || '';
      tempUserProfile.surname = nameParts.slice(1).join(' ');
    }
    tempUserProfile.email = user.email;
    tempUserProfile.phone_number = user.phoneNumber;
    tempUserProfile.language = language;
    const newUserProfile = await ApiService.post('/user/', tempUserProfile, user);
    if (newUserProfile) {
      setUserProfile(newUserProfile);
      db.set(cacheKey, newUserProfile).catch(() => {});
    }
  }, [language, applyProfile, userProfile])

  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setLanguage(lng);
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      getUserProfile(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [getUserProfile]);

  if (loading) {
    return (
      <div className="login-container">
        <div className="top-section">
          <img src="/icons/icon-512.png" alt="logo" className="logo" />
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <OfflineBanner />
      <SwUpdateToast />
      <Routes>
        <Route path="/" element={user ? <Navigate to="/homepage" replace /> : <Login setLanguage={setLanguage} />} />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute user={user}>
              <Profile user={user} userProfile={userProfile} setUserProfile={setUserProfile} setLanguage={setLanguage} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/homepage" 
          element={
            <ProtectedRoute user={user}>
              <Homepage user={user} userProfile={userProfile} />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/offline-content"
          element={
            <ProtectedRoute user={user}>
              <OfflineContentScreen user={user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suggested-routes"
          element={
            <ProtectedRoute user={user}>
              <SuggestedRoutes user={user} />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
